/* Pure geometric predicates used by the visibility walk. No DOM state, no caching:
 * everything here is a function of its arguments, so it's trivially unit-testable. */

export type LazyRect = () => DOMRect;

/** Lazily compute and memoise an element's bounding rect. */
export const lazyRect = (el: HTMLElement): LazyRect => {
  let rect: DOMRect | null = null;
  return (): DOMRect => (rect = rect ?? el.getBoundingClientRect());
};

/** Overlap test restricted to the given axes: a clipper only
 * hides content it clips on the axes it actually clips */
export const containedInAncestor = (rect: DOMRect, ancestorRect: DOMRect, axes = { x: true, y: true }): boolean =>
  (!axes.x || (rect.left <= ancestorRect.right && rect.right >= ancestorRect.left)) && (!axes.y || (rect.top <= ancestorRect.bottom && rect.bottom >= ancestorRect.top));

export const isNegligibleRect = (rect: DOMRect) => rect.width <= 1 || rect.height <= 1;

/** Scroll metrics of a scroll container, the only fields `scrollerReachable` needs
 * (typed structurally so it's trivially unit-testable without a real element). */
export type ScrollMetrics = Pick<HTMLElement, "scrollLeft" | "scrollTop" | "scrollWidth" | "scrollHeight">;

/** Can a scroll container (`scrollerRect` = its border box, `metrics` = its scroll
 * state) scroll `target` into view? Scrollable overflow only extends toward positive
 * offsets, so express `target` in the scroller's content coordinates and apply the
 * same strict one-visible-pixel bounds as `isReachable`. */
export const scrollerReachable = (target: DOMRect, scrollerRect: DOMRect, metrics: ScrollMetrics): boolean => {
  const cx = target.x - scrollerRect.x + metrics.scrollLeft;
  const cy = target.y - scrollerRect.y + metrics.scrollTop;
  return cx + target.width > 0 && cx < metrics.scrollWidth && cy + target.height > 0 && cy < metrics.scrollHeight;
};

const HIDDEN_CLIP = ["rect(0px, 0px, 0px, 0px)", "rect(0, 0, 0, 0)", "rect(1px, 1px, 1px, 1px)"];
const HIDDEN_CLIP_PATH = ["circle(0", "polygon()", "inset(100%)", "inset(50% 50% 50% 50%)"];

export const isClipped = (clip: string, path: string) => {
  if (clip && clip !== "auto") return HIDDEN_CLIP.some((value) => clip.includes(value));
  if (path && path !== "none") return HIDDEN_CLIP_PATH.some((value) => path.includes(value));
  return false;
};
